import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { suggestRoutes, type RouteSuggestion, type RouteDestination } from "@/lib/api";
import { RouteMap } from "@/components/RouteMap";

const TYPEWRITER_LINES = [
  "📍 Buscando destinos...",
  "✈️ Consultando precios reales...",
  "🗺️ Calculando rutas con paradas que valen la pena...",
  "🤖 Sift Trip está armando tus opciones perfectas...",
];

export const Route = createFileRoute("/routes")({
  component: RoutesPage,
  head: () => ({
    meta: [
      { title: "Rutas sugeridas — Sift Trip" },
      { name: "description", content: "Sift Trip encontró las mejores rutas para tu presupuesto con precios reales." },
    ],
  }),
});

function getDestinations(route: RouteSuggestion): RouteDestination[] {
  if (route.destinations && route.destinations.length > 0) return route.destinations;
  if (route.cities) return route.cities.map((c) => ({ city: c.city, iata: c.iata, nights: c.nights }));
  return [];
}

function getCost(route: RouteSuggestion): number | undefined {
  return route.total_estimated_usd ?? route.estimated_cost;
}

function getName(route: RouteSuggestion): string {
  return route.route_name || route.name || "Ruta";
}

function RoutesPage() {
  const rawSearch = Route.useSearch() as Record<string, string>;
  const navigate = useNavigate();
  const region = rawSearch.region || "Europa";
  const days = Number(rawSearch.days) || 7;
  const budget = Number(rawSearch.budget) || 2000;
  const travelers = Number(rawSearch.travelers) || 2;
  const origin = rawSearch.origin || "BOG";
  const mood = rawSearch.mood || "cultura";

  const [routes, setRoutes] = useState<RouteSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typewriterIdx, setTypewriterIdx] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<number>(0);

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => setTypewriterIdx((p) => (p + 1) % TYPEWRITER_LINES.length), 3000);
    return () => clearInterval(interval);
  }, [loading]);

  const fetchRoutes = async () => {
    setLoading(true); setError("");
    try {
      const res = await suggestRoutes({ region, budget_usd: budget, days, travelers, origin_iata: origin, mood });
      setRoutes(res.routes || []);
    } catch {
      setError("😅 Sift Trip tuvo un problema. ¿Intentamos de nuevo?");
    }
    setLoading(false);
  };

  useEffect(() => { fetchRoutes(); }, [region, budget, days, travelers, origin, mood]);

  const cheapestCost = routes.reduce<number | null>((min, r) => {
    const cost = getCost(r);
    if (!cost) return min;
    return min === null ? cost : Math.min(min, cost);
  }, null);

  const selectedRouteData = routes[selectedRoute];
  const mapPins = selectedRouteData
    ? getDestinations(selectedRouteData).filter((d) => d.lat && d.lng).map((d, i) => ({ city: d.city, lat: d.lat!, lng: d.lng!, order: i + 1, iata: d.iata, nights: d.nights }))
    : [];

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold">Sift Trip encontró <span className="text-gold">{loading ? "..." : routes.length}</span> rutas para ti</h1>
          <p className="text-muted-foreground mt-2">Basado en tu presupuesto de <span className="text-gold font-semibold">${budget}</span> para {days} días en {region}</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mb-8" />
            <motion.p key={typewriterIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-lg text-muted-foreground">
              {TYPEWRITER_LINES[typewriterIdx]}
            </motion.p>
            <div className="w-64 h-1.5 rounded-full bg-accent mt-6 overflow-hidden">
              <div className="h-full bg-gold rounded-full animate-pulse" style={{ width: "60%" }} />
            </div>
          </div>
        ) : error ? (
          <div className="rhea-card-static p-8 text-center">
            <p className="text-lg mb-2">😅</p>
            <p className="text-muted-foreground mb-2">{error}</p>
            <button onClick={fetchRoutes} className="btn-gold">Reintentar</button>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-6">
              {routes.map((route, i) => {
                const dests = getDestinations(route);
                const cost = getCost(route);
                const withinBudget = route.within_budget ?? (!cost || cost <= budget);
                const overAmount = cost ? Math.max(0, cost - budget) : 0;
                const pctUsed = route.budget_used_pct ?? (cost ? Math.min((cost / budget) * 100, 100) : 50);
                const isCheapest = cost === cheapestCost;

                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} className={`rhea-card p-6 flex flex-col cursor-pointer ${selectedRoute === i ? "ring-2 ring-gold" : ""}`} onClick={() => setSelectedRoute(i)}>
                    {isCheapest && withinBudget && <div className="self-start px-3 py-1 rounded-full text-xs font-bold badge-low mb-3">⭐ Mejor opción</div>}
                    <h3 className="text-lg font-bold mb-1">{getName(route)}</h3>
                    {route.tagline && <p className="text-sm text-muted-foreground mb-4">{route.tagline}</p>}

                    <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2">
                      {dests.map((c, ci) => (
                        <div key={ci} className="flex items-center gap-1 shrink-0">
                          {ci > 0 && <div className="w-6 h-px bg-gold" />}
                          <div className="text-center">
                            <div className="w-3 h-3 rounded-full bg-gold mx-auto" />
                            <div className="text-xs font-medium mt-1">{c.city}</div>
                            <div className="text-xs text-muted-foreground">{c.nights}n</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Presupuesto usado</span>
                        <span className={withinBudget ? "price-low" : overAmount > 200 ? "price-high" : "price-mid"}>
                          {withinBudget ? "✅ Dentro del presupuesto" : `⚠️ $${overAmount} sobre presupuesto`}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-accent overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${withinBudget ? "bg-price-low" : overAmount > 200 ? "bg-price-high" : "bg-price-mid"}`} style={{ width: `${Math.min(pctUsed, 100)}%` }} />
                      </div>
                    </div>

                    <div className="text-2xl font-bold text-gold mb-2">${cost?.toLocaleString() || "—"} <span className="text-sm font-normal text-muted-foreground">estimado</span></div>

                    {route.transport_notes && <p className="text-xs text-muted-foreground mb-2">🚆 {route.transport_notes}</p>}
                    {route.rhea_tip && <div className="p-3 rounded-xl bg-accent/50 border border-gold/20 text-sm mb-4">💡 {route.rhea_tip}</div>}

                    <div className="mt-auto pt-4">
                      <button onClick={(e) => { e.stopPropagation(); navigate({ to: "/itinerary", search: { destinations: JSON.stringify(dests.map((d) => ({ city: d.city, country: d.country || "", iata: d.iata, nights: d.nights }))), travelers: String(travelers), budget: String(budget), origin, mood } }); }} className="btn-gold w-full">Explorar esta ruta →</button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {mapPins.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-10">
                <h2 className="text-xl font-bold mb-4">🗺️ Mapa de la ruta</h2>
                <RouteMap pins={mapPins} />
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

